/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import ScrollBar from 'react-perfect-scrollbar';
import {
    faMagnifyingGlassMinus,
    faMagnifyingGlassPlus,
    faRotateLeft,
    faRotateRight,
    faArrowsMaximize,
    faAdjust,
    faDownLeftAndUpRightToCenter,
    faDownload,
    faArrowUTurnDownLeft,
} from '@fortawesome/sharp-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type SxProps, Box, IconButton, Tooltip, Typography } from '@mui/material';

type IconSizeProps = 'small' | 'medium' | 'large';

export type ImageViewerProps = {
    url: string;
    alt?: string;
    title?: string;
    iconSize?: IconSizeProps;
    sx?: SxProps;
    showTitle?: boolean;
    showTools?: boolean;
};

export const ImageViewer: React.FunctionComponent<ImageViewerProps> = function ({
    url,
    alt,
    title,
    iconSize = 'small',
    sx,
    showTitle = true,
    showTools = true,
}) {
    const [scale, setScale] = React.useState<number>(1);
    const [rotate, setRotate] = React.useState<number>(0);
    const [invert, setInvert] = React.useState<boolean>(false);
    const [position, setPosition] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState<boolean>(false);
    const [dragStart, setDragStart] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [containerDimensions, setContainerDimensions] = React.useState<{
        width: number;
        height: number;
    }>({
        width: 0,
        height: 0,
    });

    const viewerRef = React.useRef<HTMLDivElement>(null);
    const imageRef = React.useRef<HTMLImageElement>(null);

    const containerRef: React.RefCallback<HTMLElement> = React.useCallback((node) => {
        if (node !== null) {
            const rect = node.getBoundingClientRect();
            setContainerDimensions({
                width: rect.width,
                height: rect.height,
            });
        }
    }, []);

    // Gestion du glisser-déposer
    const handleMouseDown = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
        },
        [position],
    );

    const handleMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (isDragging) {
                e.preventDefault();
                setPosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y,
                });
            }
        },
        [isDragging, dragStart],
    );

    const handleMouseUp = React.useCallback(() => {
        setIsDragging(false);
    }, []);

    // Event listeners pour le drag global
    React.useEffect(() => {
        if (!isDragging) return undefined;

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mouseleave', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mouseleave', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const onSetScale = (type: number) => {
        let newScale = type ? scale + 0.1 : scale - 0.1;

        if (newScale > 5) {
            newScale = 5;
        } else if (newScale < 0.1) {
            newScale = 0.1;
        }

        setScale(newScale);

        // Reset position quand on revient au zoom normal
        if (newScale <= 1) {
            setPosition({ x: 0, y: 0 });
        }
    };

    const handleReset = () => {
        setScale(1);
        setRotate(0);
        setInvert(false);
        setPosition({ x: 0, y: 0 });
    };

    const handleFitToContainer = () => {
        if (imageRef.current && containerDimensions.width && containerDimensions.height) {
            const img = imageRef.current;
            const containerAspectRatio = containerDimensions.width / containerDimensions.height;
            const imageAspectRatio = img.naturalWidth / img.naturalHeight;

            let newScale;
            if (imageAspectRatio > containerAspectRatio) {
                // L'image est plus large que le container
                newScale = (containerDimensions.width * 0.9) / img.naturalWidth;
            } else {
                // L'image est plus haute que le container
                newScale = (containerDimensions.height * 0.9) / img.naturalHeight;
            }

            const finalScale = Math.min(Math.max(newScale, 0.1), 5);
            setScale(finalScale);

            // Reset position
            setPosition({ x: 0, y: 0 });
        }
    };

    const handleFullscreen = () => {
        if (viewerRef.current) {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else {
                viewerRef.current.requestFullscreen?.();
            }
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = title || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Empêcher le comportement par défaut de drag de l'image
    const handleDragStart = React.useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const handleContextMenu = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
    }, []);

    // Déterminer le curseur en fonction du zoom et du drag
    const getCursor = () => {
        if (isDragging) return 'grabbing';
        return 'grab';
    };

    const imageStyle: React.CSSProperties = {
        transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotate}deg)`,
        filter: invert ? 'invert(1)' : 'none',
        transition: isDragging ? 'none' : 'transform 0.2s ease, filter 0.2s ease',
        maxWidth: 'none',
        height: 'auto',
        cursor: getCursor(),
        userSelect: 'none', // Empêche la sélection de texte/image
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    };

    return (
        <Box
            role="application"
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                ...sx,
            }}
        >
            <Box
                ref={viewerRef}
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {(showTitle || showTools) && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            width: '100%',
                            marginBottom: 1,
                        }}
                    >
                        {showTitle && title && (
                            <Typography
                                color="secondary"
                                fontSize="large"
                                fontWeight="bold"
                            >
                                {title}
                            </Typography>
                        )}
                        {showTools && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row-reverse',
                                    marginLeft: 'auto',
                                }}
                            >
                                <Tooltip
                                    title="Télécharger l'image"
                                    placement="bottom-start"
                                >
                                    <IconButton
                                        data-testid="download"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="download"
                                        onClick={handleDownload}
                                    >
                                        <FontAwesomeIcon icon={faDownload} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title="Plein écran"
                                    placement="bottom-start"
                                >
                                    <IconButton
                                        data-testid="fullscreen"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="fullscreen"
                                        onClick={handleFullscreen}
                                    >
                                        <FontAwesomeIcon icon={faArrowsMaximize} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title="Ajuster à l'écran"
                                    placement="bottom-start"
                                >
                                    <IconButton
                                        data-testid="fit-to-screen"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="fit to screen"
                                        onClick={handleFitToContainer}
                                    >
                                        <FontAwesomeIcon icon={faDownLeftAndUpRightToCenter} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title="Inverser les couleurs"
                                    placement="bottom-start"
                                >
                                    <IconButton
                                        data-testid="invert-colors"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="invert colors"
                                        onClick={() => setInvert(!invert)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faAdjust}
                                            swapOpacity={invert}
                                        />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title="Réinitialiser"
                                    placement="bottom-start"
                                >
                                    <IconButton
                                        data-testid="reset"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="reset"
                                        onClick={handleReset}
                                    >
                                        <FontAwesomeIcon icon={faArrowUTurnDownLeft} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={`Zoomer (${Math.round((scale + 0.1) * 100)}%)`}>
                                    <IconButton
                                        data-testid="zoomIn"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="zoom in"
                                        onClick={() => onSetScale(1)}
                                    >
                                        <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={`Dézoomer (${Math.round((scale - 0.1) * 100)}%)`}>
                                    <IconButton
                                        data-testid="zoomOut"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="zoom out"
                                        onClick={() => onSetScale(0)}
                                    >
                                        <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title={`Tourner à gauche (${(((rotate - 90) % 360) + 360) % 360}°)`}
                                >
                                    <IconButton
                                        data-testid="rotationLeft"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="rotate left"
                                        onClick={() =>
                                            setRotate((((rotate - 90) % 360) + 360) % 360)
                                        }
                                    >
                                        <FontAwesomeIcon icon={faRotateLeft} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={`Tourner à droite (${(rotate + 90) % 360}°)`}>
                                    <IconButton
                                        data-testid="rotationRight"
                                        size={iconSize}
                                        color="secondary"
                                        aria-label="rotate right"
                                        onClick={() => setRotate((rotate + 90) % 360)}
                                    >
                                        <FontAwesomeIcon icon={faRotateRight} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                )}

                <Box
                    ref={containerRef}
                    sx={{
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <ScrollBar>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '100%',
                                padding: 2,
                            }}
                        >
                            <img
                                ref={imageRef}
                                data-testid="image-viewer"
                                style={imageStyle}
                                src={url}
                                alt={alt || title || ''}
                                onLoad={() => {
                                    // Optionnel: ajuster automatiquement au chargement
                                    // handleFitToContainer();
                                }}
                                onMouseDown={handleMouseDown}
                                onDragStart={handleDragStart}
                                onContextMenu={handleContextMenu}
                            />
                        </Box>
                    </ScrollBar>
                </Box>
            </Box>
        </Box>
    );
};
